/**
 * ATL-819 — resolve the index of a RENDERED item within the array the writer will persist.
 *
 * The FlatList is fed `data`, which `handleSearch` replaces with a FILTERED array, so `renderItem`'s
 * `index` is an index into the *filtered* array. Every consumer of `detailIndex` uses it positionally
 * against `allData` — `saveWallets`/`deleteWallet`, `saveRoles`, `saveRecurringSpendings`,
 * `savePaymentMethods`, `savePurchaseMethods`, `saveProductUnits`, `saveProductModifiers` all do
 * `updatedData[index] = info` / `updatedData.splice(index, 1)` and write the whole array back to
 * `constants/<doc>.data`. Handing a filtered index together with the unfiltered array overwrote or
 * deleted a DIFFERENT element than the one the user tapped, in Firestore — still the live read path.
 * (The Supabase leg keys on `info.id`, so the two stores then disagreed about two different rows.)
 * The index is also sent to Supabase as `displayOrder`, so it was wrong there too.
 *
 * Resolution is by OBJECT IDENTITY, deliberately not by `id`. `data` is always either `initialData`
 * itself or `initialData.filter(...)`, so every rendered item IS an element of `initialData` and
 * `indexOf` is exact and total. Keying on `id` — as in the naive `findIndex(x => x.id === item.id)` —
 * is UNSOUND for the id-less population (ATL-799 / ATL-810: 298 of 306 live `recurringSpendings`
 * elements carry no `id` at all): with both sides `undefined` it resolves EVERY id-less item to the
 * first id-less element, i.e. the same wrong-row write in a new costume.
 *
 * `id` is therefore only a fallback, only when the id is actually present, and only when it is
 * unique in the list — an ambiguous or absent match returns -1 rather than a guess. Guessing here
 * writes over a row the user never opened.
 */
export const resolveRealIndex = (item, sourceData) => {
  if (!Array.isArray(sourceData)) return -1
  const byIdentity = sourceData.indexOf(item)
  if (byIdentity !== -1) return byIdentity
  const id = item?.id
  if (id === undefined || id === null || id === '') return -1
  let match = -1
  for (let i = 0; i < sourceData.length; i++) {
    if (sourceData[i]?.id !== id) continue
    if (match !== -1) return -1 // ambiguous: two elements share this id, so neither can be addressed
    match = i
  }
  return match
}
